// src/pages/lms/LMSCalendar.jsx — Beautiful Academic Calendar
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import { Plus, X, ChevronLeft, ChevronRight, Trash2, Edit, Calendar } from 'lucide-react';

const EVENT_TYPES = [
  { value:'holiday',           label:'Holiday',            emoji:'🏖️',  color:'#10B981' },
  { value:'holiday_national',  label:'National Holiday',   emoji:'🇵🇰', color:'#10B981' },
  { value:'holiday_religious', label:'Religious Holiday',  emoji:'🕌',  color:'#059669' },
  { value:'exam',              label:'Exam',               emoji:'📝',  color:'#EF4444' },
  { value:'result',            label:'Result',             emoji:'📊',  color:'#7C3AED' },
  { value:'fee_deadline',      label:'Fee Deadline',       emoji:'💰',  color:'#F59E0B' },
  { value:'enrollment',        label:'Enrollment',         emoji:'📋',  color:'#4F8EF7' },
  { value:'semester_start',    label:'Semester Start',     emoji:'🎓',  color:'#4F8EF7' },
  { value:'semester_end',      label:'Semester End',       emoji:'🏁',  color:'#6366F1' },
  { value:'event',             label:'Event',              emoji:'🎉',  color:'#EC4899' },
  { value:'meeting',           label:'Meeting',            emoji:'🤝',  color:'#64748B' },
  { value:'sports',            label:'Sports',             emoji:'⚽',  color:'#F97316' },
  { value:'cultural',          label:'Cultural',           emoji:'🎭',  color:'#8B5CF6' },
  { value:'workshop',          label:'Workshop',           emoji:'🔧',  color:'#22D3EE' },
  { value:'other',             label:'Other',              emoji:'📌',  color:'#94A3B8' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const typeInfo = (type) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[EVENT_TYPES.length-1];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDay    = (year, month) => new Date(year, month, 1).getDay();

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
};

export default function LMSCalendar() {
  const { account, institution } = useLMSAuth();
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [selDay, setSelDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [success,setSuccess]= useState('');
  const [filterType, setFilterType] = useState('all');
  const [animDir, setAnimDir] = useState(null); // 'left'|'right'
  const [animating, setAnimating] = useState(false);

  const emptyForm = {
    title:'', description:'', event_type:'event',
    start_date:'', end_date:'', start_time:'', end_time:'',
    color:'#4F8EF7', emoji:'📅', is_important:false,
    is_holiday:false, is_published:true,
    target_degree:'all', target_class_id:'',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { if (institution?.id) load(); }, [institution?.id]);

  const load = async () => {
    try {
      const { data } = await supabase.from('lms_calendar_events')
        .select('*')
        .eq('institution_id', institution.id)
        .eq('is_published', true)
        .order('start_date');
      setEvents(data || []);
    } catch(e) { console.error(e); }
  };

  // Get events for a specific date
  const getEventsForDate = (dateStr) =>
    events.filter(e => {
      if (e.start_date === dateStr) return true;
      if (e.end_date && e.start_date <= dateStr && e.end_date >= dateStr) return true;
      return false;
    }).filter(e => filterType === 'all' || e.event_type === filterType);

  const allEventsFiltered = events.filter(e => filterType === 'all' || e.event_type === filterType);

  // Navigation with animation
  const navigate = (dir) => {
    if (animating) return;
    setAnimDir(dir);
    setAnimating(true);
    setTimeout(() => {
      if (dir === 'right') {
        if (month === 11) { setMonth(0); setYear(y => y+1); }
        else setMonth(m => m+1);
      } else {
        if (month === 0) { setMonth(11); setYear(y => y-1); }
        else setMonth(m => m-1);
      }
      setAnimDir(null);
      setTimeout(() => setAnimating(false), 300);
    }, 150);
  };

  const save = async () => {
    if (!form.title.trim())  { setError('Title required'); return; }
    if (!form.start_date)    { setError('Start date required'); return; }
    setSaving(true); setError('');
    try {
      const ti = typeInfo(form.event_type);
      const data = {
        ...form,
        institution_id:         institution.id,
        institution_account_id: account.id,
        color:                  form.color || ti.color,
        emoji:                  form.emoji || ti.emoji,
        end_date:               form.end_date || null,
        start_time:             form.start_time || null,
        end_time:               form.end_time || null,
        target_class_id:        form.target_class_id || null,
        is_holiday:             ['holiday','holiday_national','holiday_religious'].includes(form.event_type),
      };
      if (editEvent) {
        await supabase.from('lms_calendar_events').update(data).eq('id', editEvent.id);
        setSuccess('Event updated!');
      } else {
        await supabase.from('lms_calendar_events').insert(data);
        setSuccess('Event added to calendar!');
      }
      await load();
      setShowForm(false); setEditEvent(null); setForm(emptyForm);
      setTimeout(() => setSuccess(''), 3000);
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Remove this event?')) return;
    await supabase.from('lms_calendar_events').delete().eq('id', id);
    setEvents(prev => prev.filter(e => e.id !== id));
    setSelDay(null);
  };

  const openAdd = (dateStr) => {
    const ti = typeInfo('event');
    setEditEvent(null);
    setForm({ ...emptyForm, start_date: dateStr, color: ti.color, emoji: ti.emoji });
    setError('');
    setShowForm(true);
  };

  const openEdit = (ev) => {
    setEditEvent(ev);
    setForm({ ...emptyForm, ...ev, end_date: ev.end_date || '', start_time: ev.start_time || '', end_time: ev.end_time || '', target_class_id: ev.target_class_id || '' });
    setError('');
    setShowForm(true);
  };

  const handleTypeChange = (type) => {
    const ti = typeInfo(type);
    setForm(f => ({ ...f, event_type: type, color: ti.color, emoji: ti.emoji }));
  };

  // Calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDay(year, month);
  const cells       = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = toDateStr(today);
  const selDateStr = selDay ? `${year}-${String(month+1).padStart(2,'0')}-${String(selDay).padStart(2,'0')}` : null;

  // Upcoming events (next 30 days)
  const upcomingStart = toDateStr(today);
  const upcomingEnd   = toDateStr(new Date(today.getTime() + 30*24*60*60*1000));
  const upcoming = allEventsFiltered
    .filter(e => e.start_date >= upcomingStart && e.start_date <= upcomingEnd)
    .slice(0, 8);

  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };
  const F   = ({ label, children, col }) => (
    <div style={{ gridColumn: col||'span 1' }}>
      <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>{label}</label>
      {children}
    </div>
  );

  return (
    <LMSLayout>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Academic Calendar</h1>
          <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>{events.length} events · Click any date to view or add events</p>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditEvent(null); setShowForm(true); setError(''); }}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          <Plus size={16} /> Add Event
        </button>
      </div>

      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:20, alignItems:'start' }}>
        {/* CALENDAR */}
        <div style={{ background:'#fff', borderRadius:20, border:'1px solid #E5E7EB', overflow:'hidden', boxShadow:'0 4px 24px rgba(0,0,0,0.06)' }}>
          {/* Month navigation */}
          <div style={{ background:'linear-gradient(135deg,#1E293B,#0F172A)', padding:'20px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <button onClick={() => navigate('left')}
              style={{ width:36, height:36, borderRadius:10, border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', transition:'all 0.2s' }}>
              <ChevronLeft size={18} />
            </button>
            <div style={{ textAlign:'center' }}>
              <div style={{ color:'#fff', fontSize:22, fontWeight:900, letterSpacing:0.5 }}>{MONTHS[month]}</div>
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:13 }}>{year}</div>
            </div>
            <button onClick={() => navigate('right')}
              style={{ width:36, height:36, borderRadius:10, border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', transition:'all 0.2s' }}>
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', background:'#F8FAFF' }}>
            {DAYS_SHORT.map(d => (
              <div key={d} style={{ padding:'10px 0', textAlign:'center', fontSize:11, fontWeight:800, color: d==='Sun'||d==='Sat' ? '#EF4444' : '#94A3B8', letterSpacing:0.5 }}>{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{
            display:'grid', gridTemplateColumns:'repeat(7,1fr)',
            opacity: animating ? 0 : 1,
            transform: animating ? `translateX(${animDir==='right' ? '-20px' : '20px'})` : 'translateX(0)',
            transition:'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          }}>
            {cells.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} style={{ minHeight:88, background: i%7===0||i%7===6 ? '#FAFAFA' : '#fff', borderRight:'1px solid #F1F5F9', borderBottom:'1px solid #F1F5F9' }} />;
              const dateStr   = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const dayEvents = getEventsForDate(dateStr);
              const isToday   = dateStr === todayStr;
              const isSel     = selDay === day;
              const isWeekend = i%7===0 || i%7===6;
              const hasHoliday= dayEvents.some(e => e.is_holiday);
              const hasImportant = dayEvents.some(e => e.is_important);

              return (
                <div key={day} onClick={() => setSelDay(isSel ? null : day)}
                  style={{
                    minHeight:88, padding:'8px 6px',
                    background: isSel ? '#EFF6FF' : isToday ? '#FFFBEB' : hasHoliday ? '#F0FDF4' : isWeekend ? '#FAFAFA' : '#fff',
                    borderRight:'1px solid #F1F5F9', borderBottom:'1px solid #F1F5F9',
                    cursor:'pointer', transition:'all 0.15s',
                    position:'relative',
                  }}>
                  {/* Today indicator */}
                  {isToday && (
                    <div style={{ position:'absolute', top:6, right:6, width:6, height:6, borderRadius:'50%', background:'#F59E0B' }} />
                  )}
                  {/* Important dot */}
                  {hasImportant && !isToday && (
                    <div style={{ position:'absolute', top:6, right:6, width:6, height:6, borderRadius:'50%', background:'#EF4444' }} />
                  )}
                  {/* Day number */}
                  <div style={{
                    width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                    background: isToday ? '#F59E0B' : isSel ? '#4F8EF7' : 'transparent',
                    color: isToday ? '#fff' : isSel ? '#fff' : isWeekend ? '#EF4444' : '#111827',
                    fontSize:13, fontWeight: isToday||isSel ? 800 : 500,
                    marginBottom:4, transition:'all 0.15s',
                  }}>
                    {day}
                  </div>
                  {/* Events */}
                  <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                    {dayEvents.slice(0,3).map((ev,ei) => (
                      <div key={ev.id} title={ev.title}
                        style={{
                          fontSize:9, fontWeight:700, padding:'1px 5px', borderRadius:4,
                          background: ev.color + '22',
                          color: ev.color,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                          borderLeft:`2px solid ${ev.color}`,
                          lineHeight:1.6,
                        }}>
                        {ev.emoji} {ev.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div style={{ fontSize:9, color:'#94A3B8', fontWeight:600, paddingLeft:4 }}>+{dayEvents.length-3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected day events panel */}
          {selDay && selDateStr && (
            <div style={{ borderTop:'1px solid #E5E7EB', padding:'16px 20px', background:'#F8FAFF', animation:'slideDown 0.2s ease' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div style={{ fontWeight:800, color:'#1E293B', fontSize:14 }}>
                  {new Date(selDateStr+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
                </div>
                <button onClick={() => openAdd(selDateStr)}
                  style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  <Plus size={12} /> Add
                </button>
              </div>
              {getEventsForDate(selDateStr).length === 0 ? (
                <div style={{ fontSize:13, color:'#94A3B8', fontStyle:'italic', textAlign:'center', padding:'12px 0' }}>No events on this day. Click "Add" to create one.</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {getEventsForDate(selDateStr).map(ev => (
                    <div key={ev.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:12, background:'#fff', border:`1.5px solid ${ev.color}30`, boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                      <div style={{ width:36, height:36, borderRadius:10, background:`${ev.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{ev.emoji}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, color:'#111827', fontSize:13 }}>{ev.title}</div>
                        <div style={{ display:'flex', gap:8, marginTop:2, flexWrap:'wrap' }}>
                          <span style={{ fontSize:10, fontWeight:700, color:ev.color, background:`${ev.color}15`, padding:'1px 7px', borderRadius:4 }}>{typeInfo(ev.event_type).label}</span>
                          {ev.start_time && <span style={{ fontSize:10, color:'#94A3B8' }}>🕐 {ev.start_time}{ev.end_time?` – ${ev.end_time}`:''}</span>}
                          {ev.is_important && <span style={{ fontSize:10, fontWeight:700, color:'#EF4444', background:'#FEF2F2', padding:'1px 7px', borderRadius:4 }}>⚠️ Important</span>}
                          {ev.is_holiday && <span style={{ fontSize:10, fontWeight:700, color:'#10B981', background:'#F0FDF4', padding:'1px 7px', borderRadius:4 }}>Holiday</span>}
                        </div>
                        {ev.description && <div style={{ fontSize:11, color:'#6B7280', marginTop:3 }}>{ev.description}</div>}
                      </div>
                      <div style={{ display:'flex', gap:5 }}>
                        <button onClick={() => openEdit(ev)} style={{ padding:'6px 8px', borderRadius:7, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer' }}><Edit size={12} color="#374151" /></button>
                        <button onClick={() => deleteEvent(ev.id)} style={{ padding:'6px 8px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}><Trash2 size={12} color="#EF4444" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT PANEL */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Go to today */}
          <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); setSelDay(today.getDate()); }}
            style={{ width:'100%', padding:'11px 0', borderRadius:12, border:'1.5px solid #E5E7EB', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:700, color:'#374151', display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
            <Calendar size={15} /> Go to Today
          </button>

          {/* Filter by type */}
          <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', padding:14 }}>
            <div style={{ fontSize:11, fontWeight:800, color:'#374151', marginBottom:10, letterSpacing:0.5 }}>FILTER BY TYPE</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              <button onClick={() => setFilterType('all')}
                style={{ padding:'4px 10px', borderRadius:6, border:`1.5px solid ${filterType==='all'?'#4F8EF7':'#E5E7EB'}`, background:filterType==='all'?'#EFF6FF':'#fff', color:filterType==='all'?'#1D4ED8':'#6B7280', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                All
              </button>
              {EVENT_TYPES.slice(0,8).map(t => (
                <button key={t.value} onClick={() => setFilterType(filterType===t.value?'all':t.value)}
                  style={{ padding:'4px 10px', borderRadius:6, border:`1.5px solid ${filterType===t.value?t.color:'#E5E7EB'}`, background:filterType===t.value?t.color+'15':'#fff', color:filterType===t.value?t.color:'#6B7280', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                  {t.emoji} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Upcoming events */}
          <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', overflow:'hidden' }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid #F3F4F6', fontWeight:800, color:'#111827', fontSize:13 }}>📅 Upcoming (30 days)</div>
            {upcoming.length === 0 ? (
              <div style={{ padding:'20px 16px', fontSize:13, color:'#9CA3AF', textAlign:'center' }}>No upcoming events</div>
            ) : (
              <div>
                {upcoming.map((ev, i) => {
                  const d = new Date(ev.start_date + 'T12:00:00');
                  const diffDays = Math.ceil((d - today) / (1000*60*60*24));
                  return (
                    <div key={ev.id} onClick={() => { setYear(d.getFullYear()); setMonth(d.getMonth()); setSelDay(d.getDate()); }}
                      style={{ padding:'11px 16px', borderBottom: i<upcoming.length-1?'1px solid #F9FAFB':'none', display:'flex', alignItems:'center', gap:10, cursor:'pointer', transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background='#F8FAFF'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div style={{ width:36, height:36, borderRadius:10, background:`${ev.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{ev.emoji}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, color:'#111827', fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.title}</div>
                        <div style={{ fontSize:10, color:'#9CA3AF', marginTop:1 }}>
                          {d.toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                          {ev.end_date && ev.end_date !== ev.start_date ? ` – ${new Date(ev.end_date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}` : ''}
                        </div>
                      </div>
                      <span style={{ fontSize:10, fontWeight:800, color: diffDays<=3?'#EF4444':diffDays<=7?'#F59E0B':'#94A3B8', whiteSpace:'nowrap' }}>
                        {diffDays===0?'Today':diffDays===1?'Tomorrow':`${diffDays}d`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Legend */}
          <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', padding:14 }}>
            <div style={{ fontSize:11, fontWeight:800, color:'#374151', marginBottom:10, letterSpacing:0.5 }}>LEGEND</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
              {EVENT_TYPES.slice(0,8).map(t => (
                <div key={t.value} style={{ display:'flex', alignItems:'center', gap:7, fontSize:11, color:'#374151' }}>
                  <div style={{ width:10, height:10, borderRadius:3, background:t.color, flexShrink:0 }} />
                  {t.emoji} {t.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Event Modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:580, maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
              <h3 style={{ margin:0, fontWeight:800, color:'#111827', fontSize:18 }}>{editEvent ? 'Edit Event' : 'Add Calendar Event'}</h3>
              <button onClick={() => { setShowForm(false); setEditEvent(null); setForm(emptyForm); }} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280" /></button>
            </div>
            <div style={{ padding:24 }}>
              {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B' }}>{error}</div>}

              {/* Event type selector */}
              <div style={{ marginBottom:18 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:8 }}>Event Type *</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                  {EVENT_TYPES.map(t => (
                    <button key={t.value} onClick={() => handleTypeChange(t.value)}
                      style={{
                        padding:'6px 12px', borderRadius:8, border:`2px solid ${form.event_type===t.value?t.color:'#E5E7EB'}`,
                        background: form.event_type===t.value?`${t.color}15`:'#fff',
                        color: form.event_type===t.value?t.color:'#6B7280',
                        fontSize:12, fontWeight:700, cursor:'pointer', transition:'all 0.15s',
                      }}>
                      {t.emoji} {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <F label="Event Title *" col="span 2">
                  <input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. Mid-Term Exams Begin, Eid Holiday" style={inp} />
                </F>
                <F label="Start Date *">
                  <input type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})} style={inp} />
                </F>
                <F label="End Date (leave empty for single day)">
                  <input type="date" value={form.end_date} onChange={e=>setForm({...form,end_date:e.target.value})} style={inp} />
                </F>
                <F label="Start Time">
                  <input type="time" value={form.start_time} onChange={e=>setForm({...form,start_time:e.target.value})} style={inp} />
                </F>
                <F label="End Time">
                  <input type="time" value={form.end_time} onChange={e=>setForm({...form,end_time:e.target.value})} style={inp} />
                </F>
                <F label="Description" col="span 2">
                  <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={2} placeholder="Optional details..." style={{ ...inp, resize:'vertical' }} />
                </F>
                <F label="Custom Emoji">
                  <input value={form.emoji} onChange={e=>setForm({...form,emoji:e.target.value})} placeholder="📅" style={{ ...inp, fontSize:20 }} />
                </F>
                <F label="Color">
                  <input type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})} style={{ ...inp, height:42, padding:4, cursor:'pointer' }} />
                </F>
                <div style={{ gridColumn:'span 2', display:'flex', gap:16, flexWrap:'wrap' }}>
                  {[
                    { key:'is_important', label:'⚠️ Mark as Important' },
                    { key:'is_published', label:'✅ Publish (visible to students)' },
                  ].map(opt => (
                    <label key={opt.key} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>
                      <input type="checkbox" checked={form[opt.key]} onChange={e=>setForm({...form,[opt.key]:e.target.checked})} style={{ width:15, height:15 }} />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button onClick={() => { setShowForm(false); setEditEvent(null); setForm(emptyForm); }}
                  style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={save} disabled={saving}
                  style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:`linear-gradient(135deg,${form.color},${form.color}99)`, color:'#fff', fontWeight:700, cursor:saving?'wait':'pointer', opacity:saving?0.7:1 }}>
                  {saving ? 'Saving...' : editEvent ? 'Update Event' : 'Add to Calendar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity:0; transform:translateY(-8px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </LMSLayout>
  );
}